/**
 * Domain Layer - OutsideTimeExportResult ValueObject
 * 時間外データスプレッドシートエクスポート結果の値オブジェクト定義
 */

/**
 * 時刻パターン情報
 */
export interface TimePatternInfo {
  /** 時 */
  readonly hour: number;
  /** 分 */
  readonly minute: number;
  /** ラベル */
  readonly label: string;
  /** 時間範囲 */
  readonly range: string;
  /** 説明 */
  readonly description: string;
}

/**
 * 時間外データエクスポート結果を表す値オブジェクト
 */
export interface OutsideTimeExportResult {
  /** 成功フラグ */
  readonly success: boolean;
  /** シート名（例: "9/28(土)7:16"） */
  readonly sheetName?: string;
  /** シートID */
  readonly sheetId?: string;
  /** スプレッドシートURL */
  readonly spreadsheetUrl?: string;
  /** 書き込まれた行数 */
  readonly rowsWritten?: number;
  /** 書き込み開始行 */
  readonly writeStartRow?: number;
  /** 処理日時（ISO 8601形式） */
  readonly processedAt?: string;
  /** 時刻パターン情報 */
  readonly timePattern?: TimePatternInfo;
  /** 時間範囲（例: "7:00-8:15"） */
  readonly timeRange?: string;
  /** エラーメッセージ */
  readonly error?: string;
  /** エラーコード */
  readonly errorCode?: string;
}

/**
 * 成功のOutsideTimeExportResultを生成する
 * @param sheetName シート名
 * @param spreadsheetUrl スプレッドシートURL
 * @param rowsWritten 書き込まれた行数
 * @param options その他のオプション
 * @returns 成功のOutsideTimeExportResult
 */
export const createSuccessExportResult = (
  sheetName: string,
  spreadsheetUrl: string,
  rowsWritten: number,
  options?: {
    sheetId?: string;
    writeStartRow?: number;
    timePattern?: TimePatternInfo;
    timeRange?: string;
  }
): OutsideTimeExportResult => ({
  success: true,
  sheetName,
  spreadsheetUrl,
  rowsWritten,
  sheetId: options?.sheetId,
  writeStartRow: options?.writeStartRow,
  processedAt: new Date().toISOString(),
  timePattern: options?.timePattern,
  timeRange: options?.timeRange,
});

/**
 * 失敗のOutsideTimeExportResultを生成する
 * @param error エラーメッセージ
 * @param errorCode エラーコード
 * @returns 失敗のOutsideTimeExportResult
 */
export const createFailureExportResult = (
  error: string,
  errorCode?: string
): OutsideTimeExportResult => ({
  success: false,
  error,
  errorCode,
  processedAt: new Date().toISOString(),
});

/**
 * OutsideTimeExportResultが成功かどうかを判定する
 * @param result OutsideTimeExportResult
 * @returns 成功の場合true
 */
export const isExportSuccessful = (result: OutsideTimeExportResult): boolean => {
  return result.success;
};

/**
 * エクスポート結果のサマリーメッセージを生成する
 * @param result OutsideTimeExportResult
 * @returns サマリーメッセージ
 */
export const getExportSummaryMessage = (result: OutsideTimeExportResult): string => {
  if (result.success) {
    return `${result.rowsWritten}件のデータを「${result.sheetName}」シートに出力しました`;
  }
  return `エクスポートに失敗しました: ${result.error}`;
};
